import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { ApiError } from '@kirin/api-client/errors';
import type { ToolEventKind } from '@kirin/api-client/contracts';
import type {
  SessionAlertRow,
  SessionAlertsResponse,
} from '@kirin/api-client/contracts/agent-sessions';
import type { PolicyKey } from '@kirin/api-client/contracts/policies';
import { POLICY_DISPLAY_NAME } from '@kirin/api-client/helpers/alerts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import { SimplePagination } from '@/components/shared/SimplePagination';
import { Loading } from '@/components/shared/placeholders/Loading';
import { ErrorFallback } from '@/components/shared/placeholders/Error';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import {
  FilePathCellValue,
  OutcomeBadge,
} from '@/components/DataTable/alerts/CellRenderers';
import { useSessionAlerts } from '@/hooks/api/useAgentSessionTimeline';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import { formatDateTime } from '@/lib/date-utils';
import { getUserDisplayName } from '@/lib/utils';
import { AlertOccurrencesModal } from '../AlertOccurrencesModal';
import { OpenSessionViewContext } from './session-view-link';

export interface SessionViewProps {
  /** Null → the personal-account scope. */
  organizationId: string | null;
  /** The occurrence the Session view was opened from. */
  occurrenceId: string;
}

const EMPTY_MESSAGE = 'No alerts found for this session.';
const NO_EVENTS_MESSAGE = 'Session events are not currently available';

function toEventKind(value: string | null): ToolEventKind | null {
  return value === 'pre' || value === 'post' ? value : null;
}

function policyLabel(policyKey: string | null): string {
  return (policyKey && POLICY_DISPLAY_NAME[policyKey as PolicyKey]) || '—';
}

function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * KIR-4785: every alert from one agent session, across policies and alert
 * groups, ordered by event time. Opened from an occurrence; the server derives
 * the session from it and enforces the caller's scope.
 */
export function SessionView({
  organizationId,
  occurrenceId,
}: SessionViewProps): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [openRow, setOpenRow] = useState<SessionAlertRow | null>(null);
  const query = useSessionAlerts(organizationId, occurrenceId, page);

  const alertsPath = organizationId
    ? `/${organizationId}/alerts`
    : '/dashboard/alerts';
  const leave = () =>
    navigate({ pathname: alertsPath, search: location.search });

  const data = query.data;
  const openSession = useMemo(
    () =>
      data
        ? {
            sessionId: data.sessionId,
            occurrenceId,
            alertsSearch: location.search,
          }
        : null,
    [data, occurrenceId, location.search]
  );

  let body: ReactNode;
  if (query.isLoading) {
    body = <Loading label="Loading session alerts..." className="py-8" />;
  } else if (query.isError && !isNotFound(query.error)) {
    body = (
      <ErrorFallback
        retry={() => query.refetch()}
        label="Failed to load session alerts."
      />
    );
  } else if (!data) {
    body = <EmptyState />;
  } else {
    body = (
      <>
        <SessionHeader data={data} />
        {data.alerts.length === 0 ? (
          <EmptyState />
        ) : (
          <SessionAlertsTable
            rows={data.alerts}
            openedFromId={occurrenceId}
            onRowClick={setOpenRow}
          />
        )}
        <SimplePagination
          page={data.page}
          limit={data.limit}
          total={data.total}
          hasMore={data.page * data.limit < data.total}
          onPrevious={() => setPage(p => Math.max(1, p - 1))}
          onNext={() => setPage(p => p + 1)}
        />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="session-view">
      <div className="flex items-start justify-between gap-2">
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 mb-1"
            onClick={leave}
          >
            <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
            Back to Alerts
          </Button>
          <h1 className="text-xl font-semibold">Session view</h1>
          <p className="text-sm text-muted-foreground">
            Every alert raised in this agent session, across policies, in the
            order the events happened.
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Close session view"
          onClick={leave}
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      {body}
      {openRow && data && (
        <OpenSessionViewContext.Provider value={openSession}>
          <AlertOccurrencesModal
            open
            alertKey={openRow.alertKey}
            organizationId={organizationId ?? undefined}
            initialSessionId={data.sessionId}
            onClose={() => setOpenRow(null)}
          />
        </OpenSessionViewContext.Provider>
      )}
    </div>
  );
}

function EmptyState(): ReactElement {
  return (
    <Card>
      <CardContent className="py-8 text-center text-sm text-muted-foreground">
        {EMPTY_MESSAGE}
      </CardContent>
    </Card>
  );
}

function HeaderField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="break-words text-sm">{children}</dd>
    </div>
  );
}

function SessionHeader({
  data,
}: {
  data: SessionAlertsResponse;
}): ReactElement {
  const timeZone = useDisplayTimeZone();
  const { summary, header } = data;
  const timeRange =
    summary.firstAlertAt && summary.lastAlertAt
      ? `${formatDateTime(summary.firstAlertAt, timeZone)} – ${formatDateTime(summary.lastAlertAt, timeZone)}`
      : '—';
  return (
    <Card data-testid="session-view-header">
      <CardContent className="flex flex-col gap-3 pt-6">
        <div className="flex flex-wrap items-center gap-1 text-sm font-medium">
          Session{' '}
          <span className="break-all font-mono text-muted-foreground">
            {data.sessionId}
          </span>
          <CopyToClipboardButton
            text={data.sessionId}
            variant="ghost"
            compactCopiedState
            className="h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-foreground"
          >
            <span className="sr-only">Copy session ID</span>
          </CopyToClipboardButton>
        </div>
        <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <HeaderField label="User">
            {summary.user ? getUserDisplayName(summary.user) : '—'}
          </HeaderField>
          <HeaderField label="Endpoint">
            {summary.endpointName ?? '—'}
          </HeaderField>
          <HeaderField label="Alerts">{summary.alertCount}</HeaderField>
          <HeaderField label="Time range">{timeRange}</HeaderField>
        </dl>
        <p className="text-sm text-muted-foreground">
          {header
            ? `${header.eventCount} event${header.eventCount === 1 ? '' : 's'} in this session, ${header.blockedCount} blocked`
            : NO_EVENTS_MESSAGE}
        </p>
      </CardContent>
    </Card>
  );
}

function SessionAlertsTable({
  rows,
  openedFromId,
  onRowClick,
}: {
  rows: SessionAlertRow[];
  openedFromId: string;
  onRowClick: (row: SessionAlertRow) => void;
}): ReactElement {
  const timeZone = useDisplayTimeZone();
  return (
    <div className="w-full min-w-0 rounded-md border">
      <Table data-testid="session-view-table">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[170px]">Time</TableHead>
            <TableHead>Policy or Group</TableHead>
            <TableHead>Title</TableHead>
            <TableHead className="w-[100px]">Severity</TableHead>
            <TableHead className="w-[160px]">Outcome</TableHead>
            <TableHead className="max-w-[360px]">File</TableHead>
            <TableHead>Endpoint</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(row => (
            <TableRow
              key={row.occurrenceId}
              className="cursor-pointer"
              tabIndex={0}
              data-testid="session-view-row"
              onClick={() => onRowClick(row)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onRowClick(row);
                }
              }}
            >
              <TableCell className="text-sm">
                <div className="flex flex-col items-start gap-1">
                  {formatDateTime(row.occurredAt, timeZone)}
                  {row.occurrenceId === openedFromId && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="secondary" className="font-normal">
                          Selected alert
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        You opened the Session view from this alert
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-sm">
                {policyLabel(row.policyKey)}
              </TableCell>
              <TableCell className="text-sm">{row.title}</TableCell>
              <TableCell>
                <SeverityBadge riskLevel={row.severity} showIcon />
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap items-center gap-1">
                  <OutcomeBadge outcome={row.outcome} />
                  {row.muted && (
                    <Badge variant="outline" className="font-normal">
                      Muted
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="max-w-[360px] text-sm">
                <div className="max-w-[360px] overflow-hidden">
                  <FilePathCellValue
                    filePath={row.filePath}
                    location={row.location}
                    eventKind={toEventKind(row.eventKind)}
                    className="text-xs"
                  />
                </div>
              </TableCell>
              <TableCell className="text-sm">
                {row.endpointName ?? '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
